import React, { useState, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  GitBranch,
  RefreshCw,
  AlertTriangle,
  TrendingUp,
  Target,
  Zap,
  Award,
  ChevronRight,
  BarChart3,
  Lightbulb,
  Clock,
} from 'lucide-react';
import { API_BASE } from '../config';
import { Link } from 'react-router-dom';

// ─── Types ─────────────────────────────────────────────────

interface SkillEntry {
  skill: string;
  demand?: number;
  proficiency?: string;
  rarity?: number;
}

interface GapRoadmapItem {
  rank: number;
  skill: string;
  demand_pct: number;
  effort: string;
  category: string;
}

interface CategoryCoverageItem {
  category: string;
  user_count: number;
  market_count: number;
  coverage_pct: number;
}

interface QuickWin {
  have: string;
  learn: string;
  rationale: string;
}

interface SkillMatrix {
  power: SkillEntry[];
  invest: SkillEntry[];
  differentiator: SkillEntry[];
  deprioritize: SkillEntry[];
}

interface SkillGapData {
  matrix: SkillMatrix;
  gap_roadmap: GapRoadmapItem[];
  category_coverage: CategoryCoverageItem[];
  quick_wins: QuickWin[];
  competitive_edge: string[];
  total_gap_skills: number;
  generated_at: string;
}

// ─── Helpers ───────────────────────────────────────────────

function formatAge(isoString: string): string {
  const ms = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  return `${Math.floor(mins / 60)}h ago`;
}

function effortColor(effort: string): string {
  if (effort.startsWith('Low')) return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800';
  if (effort.startsWith('High')) return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
  return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
}

function categoryColor(category: string): string {
  const cat = (category || '').toLowerCase();
  if (cat === 'technical' || cat === 'tool') return 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400';
  if (cat === 'soft' || cat === 'soft_skill') return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400';
  if (cat === 'leadership') return 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400';
  if (cat === 'certif') return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400';
  return 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400';
}

function proficiencyDots(proficiency: string | undefined): React.ReactNode {
  const levels: Record<string, number> = { beginner: 1, intermediate: 2, advanced: 3, expert: 4 };
  const count = levels[proficiency?.toLowerCase() ?? ''] ?? 2;
  return (
    <span className="flex gap-0.5">
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          className={`w-1.5 h-1.5 rounded-full ${i <= count ? 'bg-green-500' : 'bg-gray-200 dark:bg-slate-600'}`}
        />
      ))}
    </span>
  );
}

// ─── Sub-Components ─────────────────────────────────────────

function QuadrantCard({
  title,
  subtitle,
  skills,
  colorClass,
  headerColorClass,
  countBadgeClass,
  emptyMessage,
  showDots,
  showDemand,
}: {
  title: string;
  subtitle: string;
  skills: SkillEntry[];
  colorClass: string;
  headerColorClass: string;
  countBadgeClass: string;
  emptyMessage: string;
  showDots?: boolean;
  showDemand?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const displaySkills = expanded ? skills : skills.slice(0, 8);

  return (
    <div className={`rounded-xl border-2 ${colorClass} h-full flex flex-col`}>
      {/* Header */}
      <div className={`px-4 py-3 rounded-t-xl ${headerColorClass}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold">{title}</p>
            <p className="text-xs mt-0.5 opacity-75">{subtitle}</p>
          </div>
          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${countBadgeClass}`}>
            {skills.length}
          </span>
        </div>
      </div>

      {/* Skills list */}
      <div className="flex-1 p-4 overflow-hidden">
        {skills.length === 0 ? (
          <p className="text-xs text-center text-gray-400 dark:text-slate-500 italic mt-4">{emptyMessage}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {displaySkills.map((s, i) => (
              <div key={i} className="flex items-center gap-1 bg-white/70 dark:bg-slate-800/70 rounded-full px-2.5 py-1 border border-white/50 dark:border-slate-700/50">
                <span className="text-xs font-medium text-gray-700 dark:text-slate-300">{s.skill}</span>
                {showDots && proficiencyDots(s.proficiency)}
                {showDemand && s.demand !== undefined && (
                  <span className="text-xs text-gray-400 dark:text-slate-500 ml-0.5">{Math.round(s.demand * 100)}%</span>
                )}
              </div>
            ))}
            {skills.length > 8 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="text-xs text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 px-2 py-1 underline"
              >
                {expanded ? 'Show less' : `+${skills.length - 8} more`}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-10 bg-gray-100 dark:bg-slate-800 rounded-xl w-2/3" />
      <div className="grid grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => <div key={i} className="bg-gray-100 dark:bg-slate-800 rounded-xl h-24" />)}
      </div>
      <div className="bg-gray-100 dark:bg-slate-800 rounded-xl h-72" />
      <div className="bg-gray-100 dark:bg-slate-800 rounded-xl h-64" />
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-gray-100 dark:bg-slate-800 rounded-xl h-48" />
        <div className="bg-gray-100 dark:bg-slate-800 rounded-xl h-48" />
      </div>
    </div>
  );
}

// ─── Main Component ─────────────────────────────────────────

const SkillGapAnalysis: React.FC = () => {
  const [data, setData] = useState<SkillGapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/skill-gap-deep`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as SkillGapData;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load skill gap analysis');
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    void fetchData().finally(() => setLoading(false));
  }, [fetchData]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await fetch(`${API_BASE}/api/intelligence/skill-gap-deep/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json() as SkillGapData;
      setData(json);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Refresh failed');
    }
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <GitBranch className="w-6 h-6 text-indigo-600" />
              Market Skill Gap Analysis
            </h1>
            <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">Analyzing your skills against live market demand...</p>
          </div>
        </div>
        <LoadingSkeleton />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-red-500 mx-auto mb-3" />
          <p className="text-red-800 dark:text-red-400 font-medium">Failed to load skill gap analysis</p>
          <p className="text-red-600 dark:text-red-500 text-sm mt-1">{error}</p>
          <button
            onClick={() => { setLoading(true); void fetchData().finally(() => setLoading(false)); }}
            className="mt-4 px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-xl text-sm font-medium hover:bg-red-200 dark:hover:bg-red-900/50"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const d = data!;
  const coveragePct = d.matrix.power.length > 0 || d.matrix.invest.length > 0
    ? Math.round((d.matrix.power.length / Math.max(d.matrix.power.length + d.matrix.invest.length, 1)) * 100)
    : 0;

  return (
    <div className="max-w-5xl mx-auto space-y-6">

      {/* ── Page Header ── */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-indigo-600" />
            Market Skill Gap Analysis
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
            Deep analysis of your skills against live job market demand
            {data && (
              <span className="ml-2 text-gray-400 dark:text-slate-500">
                · Last analyzed {formatAge(d.generated_at)}
              </span>
            )}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => void handleRefresh()}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-60 transition-colors shadow-sm"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh Analysis'}
        </motion.button>
      </div>

      {/* ── Summary Banner ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 px-4 py-4">
          <div className="flex items-center gap-2 mb-1">
            <Award className="w-4 h-4 text-green-500" />
            <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Power Skills</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{d.matrix.power.length}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">High demand, you have them</p>
        </div>
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 px-4 py-4">
          <div className="flex items-center gap-2 mb-1">
            <Target className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Skill Gaps</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{d.total_gap_skills}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">High demand, you're missing</p>
        </div>
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 px-4 py-4">
          <div className="flex items-center gap-2 mb-1">
            <BarChart3 className="w-4 h-4 text-primary-500" />
            <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Market Coverage</span>
          </div>
          <p className="text-2xl font-bold text-primary-600">{coveragePct}%</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">Of demanded skills you have</p>
        </div>
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 px-4 py-4">
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-4 h-4 text-indigo-500" />
            <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Quick Wins</span>
          </div>
          <p className="text-2xl font-bold text-indigo-600">{d.quick_wins.length}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">Skills you can learn fast</p>
        </div>
      </div>

      {/* ── 2×2 Skills Matrix ── */}
      <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Skills Market Matrix</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            Where your skills sit relative to what the market demands right now
          </p>
        </div>

        {/* Axis labels */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            {/* High demand label */}
            <div className="flex items-center justify-center mb-2">
              <div className="flex items-center gap-1.5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-full px-3 py-1">
                <TrendingUp className="w-3.5 h-3.5 text-amber-600" />
                <span className="text-xs font-bold text-amber-700 dark:text-amber-400">HIGH MARKET DEMAND</span>
              </div>
            </div>

            {/* 2×2 Grid */}
            <div className="grid grid-cols-2 gap-3" style={{ minHeight: '340px' }}>
              {/* Top-left: Power Skills */}
              <QuadrantCard
                title="Power Skills"
                subtitle="You have them + Market wants them"
                skills={d.matrix.power}
                colorClass="border-green-300 dark:border-green-800 bg-green-50/50 dark:bg-green-900/20"
                headerColorClass="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400"
                countBadgeClass="bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-300"
                emptyMessage="Upload resumes or complete the interview to identify power skills"
                showDots
              />

              {/* Top-right: Invest Now */}
              <QuadrantCard
                title="Invest Now"
                subtitle="Market wants them + You're missing them"
                skills={d.matrix.invest}
                colorClass="border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-900/20"
                headerColorClass="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-400"
                countBadgeClass="bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-300"
                emptyMessage="No critical skill gaps detected — great coverage!"
                showDemand
              />
            </div>

            {/* Low demand divider */}
            <div className="flex items-center justify-center my-3">
              <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700" />
              <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-full px-3 py-1 mx-3">
                <span className="text-xs font-bold text-gray-500 dark:text-slate-400">LOW MARKET DEMAND</span>
              </div>
              <div className="flex-1 h-px bg-gray-200 dark:bg-slate-700" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Bottom-left: Differentiators */}
              <QuadrantCard
                title="Your Differentiators"
                subtitle="You have them + Niche competitive value"
                skills={d.matrix.differentiator}
                colorClass="border-primary-300 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/20"
                headerColorClass="bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-400"
                countBadgeClass="bg-primary-200 dark:bg-primary-800 text-primary-800 dark:text-primary-300"
                emptyMessage="No differentiator skills found yet"
                showDots
              />

              {/* Bottom-right: Deprioritize */}
              <QuadrantCard
                title="Deprioritize"
                subtitle="You have them + Low market relevance"
                skills={d.matrix.deprioritize}
                colorClass="border-gray-300 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50"
                headerColorClass="bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400"
                countBadgeClass="bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-slate-400"
                emptyMessage="No deprioritized skills found"
              />
            </div>

            {/* Y-axis label (left) */}
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 -translate-x-full">
              <div className="flex items-center gap-1" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                <span className="text-xs font-bold text-gray-400 dark:text-slate-500 tracking-wider">YOU HAVE IT →</span>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-4">
          <p className="text-xs text-gray-400 dark:text-slate-500">
            Skills classified using live data from active job postings. Demand % = share of jobs requiring this skill.
          </p>
        </div>
      </div>

      {/* ── Gap Prioritization Roadmap ── */}
      <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Gap Prioritization Roadmap</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            Skills to invest in, ranked by market demand and learning efficiency
          </p>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-slate-800">
          {d.gap_roadmap.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <Award className="w-8 h-8 text-green-500 mx-auto mb-2" />
              <p className="text-gray-600 dark:text-slate-400 font-medium">Excellent market coverage!</p>
              <p className="text-gray-400 dark:text-slate-500 text-sm mt-1">No critical skill gaps detected in the current market.</p>
            </div>
          ) : (
            d.gap_roadmap.map((item) => (
              <motion.div
                key={item.rank}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: item.rank * 0.05 }}
                className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors"
              >
                {/* Rank */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  item.rank <= 3 ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' :
                  item.rank <= 7 ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                  'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400'
                }`}>
                  {item.rank}
                </div>

                {/* Skill name + category */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-gray-900 dark:text-white">{item.skill}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${categoryColor(item.category)}`}>
                      {item.category}
                    </span>
                  </div>
                </div>

                {/* Demand bar */}
                <div className="w-32 flex-shrink-0">
                  <div className="flex justify-between mb-1">
                    <span className="text-xs text-gray-500 dark:text-slate-400">Market demand</span>
                    <span className="text-xs font-bold text-gray-700 dark:text-slate-300">{item.demand_pct}%</span>
                  </div>
                  <div className="h-1.5 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full rounded-full ${
                        item.demand_pct >= 60 ? 'bg-red-500' :
                        item.demand_pct >= 40 ? 'bg-amber-500' :
                        'bg-primary-400'
                      }`}
                      initial={{ width: 0 }}
                      animate={{ width: `${item.demand_pct}%` }}
                      transition={{ delay: item.rank * 0.05 + 0.3 }}
                    />
                  </div>
                </div>

                {/* Effort badge */}
                <span className={`text-xs px-2.5 py-1 rounded-full border font-medium flex-shrink-0 ${effortColor(item.effort)}`}>
                  <Clock className="w-3 h-3 inline mr-1" />
                  {item.effort}
                </span>
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* ── Quick Win Gaps + Competitive Edge ── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

        {/* Quick Wins */}
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-800 bg-green-50 dark:bg-green-900/20">
            <h2 className="text-base font-bold text-green-900 dark:text-green-400 flex items-center gap-2">
              <Zap className="w-4 h-4 text-green-600" />
              Quick Win Gaps
            </h2>
            <p className="text-xs text-green-700 dark:text-green-500 mt-0.5">
              Skills you can learn fast because of what you already know
            </p>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-slate-800">
            {d.quick_wins.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="text-sm text-gray-400 dark:text-slate-500 italic">
                  Upload more resumes or complete an AI interview to surface quick wins.
                </p>
              </div>
            ) : (
              d.quick_wins.map((win, i) => (
                <div key={i} className="px-5 py-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-2 py-0.5 rounded-full font-medium">
                      {win.have}
                    </span>
                    <ChevronRight className="w-3.5 h-3.5 text-gray-400 dark:text-slate-500" />
                    <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-medium">
                      {win.learn}
                    </span>
                    <span className="ml-auto text-xs bg-green-50 dark:bg-green-900/20 text-green-600 border border-green-200 dark:border-green-800 px-2 py-0.5 rounded-full font-medium">
                      Quick
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-slate-400 leading-relaxed">{win.rationale}</p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Competitive Edge */}
        <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-800 bg-primary-50 dark:bg-primary-900/20">
            <h2 className="text-base font-bold text-primary-900 dark:text-primary-400 flex items-center gap-2">
              <Award className="w-4 h-4 text-primary-600" />
              Your Competitive Edge
            </h2>
            <p className="text-xs text-primary-700 dark:text-primary-500 mt-0.5">
              Skills that differentiate you — few candidates have these
            </p>
          </div>
          <div className="p-5">
            {d.competitive_edge.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-slate-500 italic text-center py-4">
                No differentiator skills identified yet. Complete more data sources.
              </p>
            ) : (
              <>
                <div className="flex flex-wrap gap-2">
                  {d.competitive_edge.map((skill, i) => (
                    <motion.span
                      key={i}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.06 }}
                      className="bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 border border-primary-200 dark:border-primary-800 text-sm font-medium px-3 py-1.5 rounded-full"
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-4 flex items-start gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                  These skills are less common in the market but you have them. Highlight them prominently
                  in your LinkedIn profile and resume to stand out from other candidates.
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Category Coverage Chart ── */}
      <div className="bg-white dark:bg-navy-800 rounded-xl border border-gray-200 dark:border-slate-800 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-800">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">Category Coverage</h2>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            How well your skills cover each category the market demands
          </p>
        </div>
        <div className="p-6 space-y-4">
          {d.category_coverage.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-4 italic">
              Category data will appear after skills are aggregated from your resumes.
            </p>
          ) : (
            d.category_coverage.map((cat, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${categoryColor(cat.category)}`}>
                      {cat.category}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-slate-400">
                      {cat.user_count} of {cat.market_count} in-demand skills
                    </span>
                  </div>
                  <span className={`text-sm font-bold ${
                    cat.coverage_pct >= 70 ? 'text-green-600' :
                    cat.coverage_pct >= 40 ? 'text-amber-600' :
                    'text-red-600'
                  }`}>
                    {cat.coverage_pct}%
                  </span>
                </div>
                <div className="h-2 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
                  <motion.div
                    className={`h-full rounded-full ${
                      cat.coverage_pct >= 70 ? 'bg-green-500' :
                      cat.coverage_pct >= 40 ? 'bg-amber-500' :
                      'bg-red-400'
                    }`}
                    initial={{ width: 0 }}
                    animate={{ width: `${Math.min(cat.coverage_pct, 100)}%` }}
                    transition={{ delay: i * 0.1, duration: 0.7 }}
                  />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Footer ── */}
      <div className="bg-gray-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-800 rounded-xl p-5 text-center">
        <p className="text-sm text-gray-600 dark:text-slate-400">
          Skill demand calculated from{' '}
          <span className="font-semibold">live active job postings</span>.
          Re-run analysis weekly to track market shifts.
        </p>
        <div className="mt-3 flex items-center justify-center gap-4">
          <Link to="/linkedin-optimizer" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium underline">
            ← LinkedIn optimizer
          </Link>
          <Link to="/intelligence" className="text-sm text-indigo-600 hover:text-indigo-800 font-medium underline">
            Intelligence dashboard →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SkillGapAnalysis;
